import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {environment} from '../../../environments/environment';
import { catchError, map } from 'rxjs/operators';
import {AuthService} from './auth.service';
import {v4 as uuidv4} from 'uuid';
import {ParamService} from './param.service';
import {properties} from './../../../environments/properties';
import { LogService } from './log.service';

@Injectable()
export class SubscriptionService {

    banner;
    currentTime = new Date();
    loadTenders$ = new BehaviorSubject(null)
    messageCenterData$: BehaviorSubject<any> = new BehaviorSubject<any>({})
    private properties = properties;

    // Handoff slot: a route guard that already fetched subscription details stashes them here so the
    // page component can render without a second Subscription API call. customerId-keyed, time-boxed,
    // read-and-clear — falls back to a live fetch whenever a fresh match isn't present.
    //
    // STATIC on purpose: SubscriptionService is `@Injectable()` (not providedIn:'root') and is
    // re-provided in some lazy feature modules (e.g. B4uplusManageModule), so the guard's root-injector
    // instance and a lazy page component's instance are DIFFERENT objects. A per-instance field would
    // never be seen across that boundary; a static slot is shared process-wide so the handoff works
    // no matter which injector created the service.
    private static stashedSubscription: { customerId: string; response: any; at: number } | null = null;
    private readonly STASH_FRESHNESS_MS = 10000;

    enableForUPlus: boolean = this.properties.featureFlags.enableForUPlus;
    programDisplayData = this.enableForUPlus ? this.properties.programType.forUPlus : this.properties.programType.freshpass;
    
    constructor(public authService: AuthService,
                private paramService: ParamService,
                public _http: HttpClient,
                private logService: LogService) {
        this.banner = this.paramService.banner;
    }

    stashSubscriptionDetails(customerId: string, response: any): void {
        SubscriptionService.stashedSubscription = customerId ? { customerId, response, at: Date.now() } : null;
    }

    consumeStashedSubscriptionDetails(customerId: string): any | null {
        const stashed = SubscriptionService.stashedSubscription;
        SubscriptionService.stashedSubscription = null; // read-and-clear regardless of match
        if (stashed && stashed.customerId === customerId && (Date.now() - stashed.at) < this.STASH_FRESHNESS_MS) {
            return stashed.response;
        }
        return null;
    }

    clearStashedSubscriptionDetails(): void {
        SubscriptionService.stashedSubscription = null;
    }

    getApplicationDomainURL(bannerOveride: string = null) {
        let domain;
        let banner = this.banner;
        if (window.location.href.indexOf('local') > 0) {
            domain = 'https://www-qa1.' + banner + '.com';
        } else {
            domain = 'https://' + window.location.host;
        }
        return domain;
    }
    
    getApplicationDomainRedirectBannerURL(bannerOveride) {
      let domain;
      let currentBanner = this.banner;
      if (window.location.href.indexOf('local') > 0) {
        domain = 'https://www-qa1.' + bannerOveride + '.com';
      } else {
        domain = 'https://' + window.location.host;
        domain = domain.replace(currentBanner, bannerOveride);
      }
      return domain;
    }

    parseJwt(jwtToken) {
        const base64Url = jwtToken.split('.')[1];
        const base64 = base64Url.replace('-', '+').replace('_', '/');
        return JSON.parse(window.atob(base64));
    }

    getUserInfoCall() {
      this.banner = this.paramService.banner;
      let url;
      let options = {};
      const crypto = window.crypto
      const array = new Uint32Array(1);

      if (window.location.href.indexOf('local') > 0) {
        // Using Unified Banners Call, if not unified, remove /unified/ from the url path
        url = this.getApplicationDomainURL() + '/bin/safeway/unified/userinfo?banner=' + this.banner + '&rand=' + crypto.getRandomValues(array);
        console.log('TokenUserInfo ->', url);
        options['withCredentials'] = true;
      } else {
        // Using Unified Banners Call, if not unified, remove /unified/ from the url path
        url = '/bin/safeway/unified/userinfo?banner=' + this.banner;
      }
      return this._http.get(url, options);
    }

    getUserClaims(user) {
        const oktaToken = this.parseJwt(user.SWY_SHOP_TOKEN);
        const userClaims = {
            aln: oktaToken['aln'] ? oktaToken['aln'] : undefined,
            hid: oktaToken['hid'] ? oktaToken['hid'] : undefined,
            zip: oktaToken['zip'] ? oktaToken['zip'] : undefined,
            gid: oktaToken['gid'] ? oktaToken['gid'] : undefined,
            uuid: oktaToken['uuid'] ? oktaToken['uuid'] : undefined,
            exp: oktaToken['exp'] ? oktaToken['exp'] : undefined,
            firstname: oktaToken['fnm'] ? oktaToken['fnm'] : undefined,
            lastname: oktaToken['lnm'] ? oktaToken['lnm'] : undefined
        };
        return userClaims;
    }

    public getUserSubscriptionDetails(userInfo, currentEnvironment): Observable<any> {
        const url = this.getApplicationDomainURL() + environment[currentEnvironment].getSubscriptionApiConfig.url.replace('{gid}', userInfo.customerId);
        let headerObj = environment[currentEnvironment].subscriptionHeaders.deliverySubHttpJavaOptions;

        this.replaceHeaderBanner(headerObj);
        const options = {
            headers: {
                ...headerObj,
                'Authorization': `Bearer ${userInfo.authToken}`,
                'x-swy-correlation-id': uuidv4()
            }
        };
        return this._http.get(url, options).pipe(
            catchError((error) => {
                this.logService.log(`Error in getUserSubscriptionDetails: ${JSON.stringify(error)}`);
                return throwError(() => error);
            })
        );
    }

    public getUserEnrollmentDetails(userInfo, planType, currentEnvironment): Observable<any> {
        const url = this.getApplicationDomainURL() + environment[currentEnvironment].getSubscriptionApiConfig.url.replace('{uuid}', userInfo.customerId).replace('{gid}', planType);
        let headerObj = environment[currentEnvironment].subscriptionHeaders.deliverySubHttpJavaOptions;

        this.replaceHeaderBanner(headerObj);
        const options = {
            headers: {
                ...headerObj,
                'Authorization': `Bearer ${userInfo.authToken}`,
                'x-swy-correlation-id': uuidv4()
            }
        };
        return this._http.get(url, options).pipe(
            catchError((error) => {
                this.logService.log(`Error in getUserEnrollmentDetails: ${JSON.stringify(error)}`);
                return throwError(() => error);
            })
        );
    }

    public getTenders(userInfo, currentEnvironment): Observable<any> {
        const url = this.getApplicationDomainURL() + environment[currentEnvironment].getTendersApiConfig.url.replace('{uuid}', userInfo.UUID)
        let headerObj = environment[currentEnvironment].subscriptionHeaders.paymentOptions;
        let isoDate = this.currentTime.toISOString();
        const options = {
            headers: {
                ...headerObj,
                'Date': `${isoDate}`,
                'storeId': '9999',
                'Authorization': `Bearer ${userInfo.authToken}`,
                'x-swy-correlation-id': uuidv4(),
                'x-swy-request-id': uuidv4()
            }
        };
        return this._http.get(url, options).pipe(
            catchError((error) => {
                this.logService.log(`Error in getTenders: ${JSON.stringify(error)}`);
                return throwError(() => error);
            })
        );
    }

    

    public getPlans(userInfo, currentEnvironment): Observable<any> {
        const url = this.getApplicationDomainURL() + environment[currentEnvironment].getPlansApiConfig.url;
        let headerObj = environment[currentEnvironment].subscriptionHeaders.deliverySubHttpJavaOptions;
        const enableSnap: boolean = properties.featureFlags.enableSnap;
        const enableB4UPlusLaunch: boolean = this.properties.featureFlags.enableB4UPlusLaunch;
        if (enableSnap || (enableB4UPlusLaunch && this.authService.checkB2B())) {
          headerObj = environment[currentEnvironment].subscriptionHeaders.deliverySubHttpJavaOptionsV4;
        }
        this.replaceHeaderBanner(headerObj);
        let currentTime = new Date();
            
        const options = {
            headers: {
                ...headerObj,
                'Authorization': `Bearer ${userInfo.authToken}`,
                'x-swy-correlation-id': uuidv4(),
                'x-swy-date': currentTime.toString()
            }
        };
        return this._http.get(url, options).pipe(
            catchError((error) => {
                this.logService.log(`Error in getPlans: ${JSON.stringify(error)}`);
                return throwError(() => error);
            })
        );
    }

    public changePlan(userInfo, requestObj, gid, currentEnvironment, webSource = null): Observable<any> {
        const url = this.getApplicationDomainURL() + environment[currentEnvironment].changePlanApiConfig.url.replace('{gid}', gid);
        let headerObj = environment[currentEnvironment].subscriptionHeaders.deliverySubHttpJavaOptions;
        this.replaceHeaderBanner(headerObj);
        const currentTime = new Date();
        let options = {
            headers: {
                ...headerObj,
                'Authorization': `Bearer ${userInfo.authToken}`,
                'x-swy-correlation-id': uuidv4(),
                'x-swy-date': currentTime.toString(),
            }
        };

        if (webSource?.param) {
            options.headers['x-swy-client-name'] = webSource?.param;
        }

        return this._http.post(url, requestObj, options).pipe(
            catchError((error) => {
                this.logService.log(`Error in changePlan: ${JSON.stringify(error)}`);
                return throwError(() => error);
            })
        );
    }

    public getUserProfileInfo(userInfo, currentEnvironment, profileOptions = ''): Observable<any> {
        let url = this.getApplicationDomainURL() + environment[currentEnvironment].getUserProfileInfo.url;
        //if profile options are passed append to url and use UUID instead of customerID.
        let headerObj = environment[currentEnvironment].subscriptionHeaders.ucaHttpOptions;
        if (profileOptions && profileOptions.length > 0) {
          url = url.replace('{gid}', userInfo.UUID);
          const separator = url.includes('?') ? '&' : '?';
          url += `${separator}${profileOptions}`;
          
          headerObj = environment[currentEnvironment].subscriptionHeaders.ucaHttpOptionsv4;
        } else {
          url = url.replace('{gid}', userInfo.customerId);
        }
         
        this.replaceHeaderBanner(headerObj);
        const options = {
            headers: {
                ...headerObj,
                'Authorization': `Bearer ${userInfo.authToken}`,
                'x-swy-correlation-id': uuidv4()
            }
        };
        return this._http.get(url, options).pipe(
            catchError((error) => {
                this.logService.log(`Error in getUserProfileInfo: ${JSON.stringify(error)}`);
                return throwError(() => error);
            })
        );
    }

    public getTerms(userInfo, currentEnvironment): Observable<any> {
        const url = this.getApplicationDomainURL() + environment[currentEnvironment].getTermsInfoApi.url + '/' + userInfo.customerId
        let headerObj = environment[currentEnvironment].subscriptionHeaders.deliverySubHttpJavaOptions;
        this.replaceHeaderBanner(headerObj);
        const options = {
            headers: {
                ...headerObj,
                'Authorization': `Bearer ${userInfo.authToken}`,
                'x-swy-correlation-id': uuidv4()
            }
        };
        return this._http.get(url, options).pipe(
          map((data) => {
            data = this.replaceBannerPlaceHolderText(data)
            return data;
          }),
          catchError((error) => {
              this.logService.log(`Error in getTerms: ${JSON.stringify(error)}`);
              return throwError(() => error);
          })
        );
    }

    public reinstateSubscription(userInfo, currentEnvironment): Observable<any> {
        const url = this.getApplicationDomainURL() + environment[currentEnvironment].reinstateAPI + userInfo.customerId;
        let headerObj = environment[currentEnvironment].subscriptionHeaders.reinstateOptions;
        this.replaceHeaderBanner(headerObj);
        const options = {
            headers: {
                ...headerObj,
                'Authorization': `Bearer ${userInfo.authToken}`,
                'x-swy-correlation-id': uuidv4()
            }
        };
        const jwtClaims = this.getUserClaims(userInfo);
        const reinstateRequestObj = {
            "createdBy": jwtClaims.firstname + ' ' + jwtClaims.lastname,
            "gid": userInfo.customerId
        };
        return this._http.post(url, reinstateRequestObj, options).pipe(
            catchError((error) => {
                this.logService.log(`Error in reinstateSubscription: ${JSON.stringify(error)}`);
                return throwError(() => error);
            })
        );
    }

    public getCancelReasons(userInfo, currentEnvironment): Observable<any> {
        const url = this.getApplicationDomainURL() + environment[currentEnvironment].getCancelReasonsApiConfigV2.url;
        let headerObj = environment[currentEnvironment].subscriptionHeaders.deliverySubHttpJavaOptions;
        this.replaceHeaderBanner(headerObj);
        const currentTime = new Date();
        const options = {
            headers: {
                ...headerObj,
                'Authorization': `Bearer ${userInfo.authToken}`,
                'x-swy-client-name': 'WEB-PORTAL',
                'x-swy-correlation-id': uuidv4(),
                'x-swy-date': currentTime.toString(),
                'x-swy-banner': this.banner,
            }
        };
        return this._http.get(url, options).pipe(
            catchError((error) => {
                this.logService.log(`Error in getCancelReasons: ${JSON.stringify(error)}`);
                return throwError(() => error);
            })
        );
    }

    public cancelSubscription(userInfo, cancelReason, cancelComments, currentEnvironment, retentionEligibilityCheck): Observable<any> {
        const url = this.getApplicationDomainURL() + environment[currentEnvironment].cancelSubscriptionPlanApiConfig.url.replace('{gid}', userInfo.customerId);
        let headerObj = environment[currentEnvironment].subscriptionHeaders.deliverySubHttpJavaOptionsV3;
        this.replaceHeaderBanner(headerObj);
        const userResponse = JSON.stringify({
            'reasonCode': cancelReason,
            'userComments': cancelComments,
            'retentionEligibilityCheck': retentionEligibilityCheck
        })
        const currentTime = new Date();
        const options = {
            headers: {
                ...headerObj,
                'Authorization': `Bearer ${userInfo.authToken}`,
                'x-swy-correlation-id': uuidv4(),
                'x-swy-date': currentTime.toString()
            }
        };

        return this._http.post(url, userResponse, options).pipe(
            catchError((error) => {
                this.logService.log(`Error in cancelSubscription: ${JSON.stringify(error)}`);
                return throwError(() => error);
            })
        );
    }

    public getFreshPassBenefitsJSONAPI(currentEnvironment): Observable<any> {
      let url;

      if (this.banner === 'jewelosco') {
        url = this.getApplicationDomainURL() + environment[currentEnvironment].freshpassLPMaster_Jewelosco_CASS;
      } else if (this.banner === 'acmemarkets')  {
        url = this.getApplicationDomainURL() + environment[currentEnvironment].freshpassLPMaster_Acme_CASS;
      } else {
        url = this.getApplicationDomainURL() + environment[currentEnvironment].aemFreshPassBenefitsJSONURL;
      }
      return this._http.get(url).pipe(
          catchError((error) => {
              this.logService.log(`Error in getFreshPassBenefitsJSONAPI: ${JSON.stringify(error)}`);
              return throwError(() => error);
          })
      );
    }

    public getB4UPlusBenefitsComponent(currentEnvironment): Observable<any> {
      const url = this.getApplicationDomainURL() + environment[currentEnvironment].aemB4UPlusBenefitsURL;
      return this._http.get(url, {responseType: 'text'}).pipe(
          map((data) => {
            data = this.replaceBannerPlaceHolderText(data);
            return data;
          }),
          catchError((error) => {
              this.logService.log(`Error in getB4UPlusBenefitsComponent: ${JSON.stringify(error)}`);
              return throwError(() => error);
          })
      );
    }

    public replaceHeaderBanner(header) {
        if (header) {
            if (header['banner']) {
                header['banner'] = header['banner'].replace(/\{banner\}/g, this.banner);
            }
            if (header['x-swy_banner']) {
                header['x-swy_banner'] = header['x-swy_banner'].replace(/\{banner\}/g, this.banner);
            }
            if (header['x-swy-banner']) {
              header['x-swy-banner'] = header['x-swy-banner'].replace(/\{banner\}/g, this.banner);
          }
        }
    }

    errorHandling = function (errorStatusCode, errorCode) {
        let message = '';
        if (errorStatusCode && errorCode) {
            // Add special case for 412 and OSMS-SUBSCRIPTION-00023
            if (errorStatusCode === 412 && errorCode === "OSMS-SUBSCRIPTION-00023") {
                message = this.properties.cardRefundError;
                message = message.replace('{NUM}', this.programDisplayData.manage.Num_855_767_2545);
            } else {
                switch (errorCode) {
                    case 'OSMS-SUBSCRIPTION-0009':
                        message = this.properties.invalidReasonCode
                        break;
                    case 'OSMS-SUBSCRIPTION-00013':
                        message = this.properties.cancelDuringBatchProcess;
                        break;
                    case 'OSMS-SUBSCRIPTION-00014':
                        message = this.properties.cancelWithinBufferTime;
                        break;
                    case 'OSMS-SUBSCRIPTION-0007':
                        message = this.properties.serverError;
                        break;
                    case 'OSMS-SUBSCRIPTION-0000':
                        message = this.properties.unknownError;
                        break;
                    case 'OSMS-SUBSCRIPTION-00021':
                        message = this.properties.subsPaymentError;
                        break;
                    case 'OSMS-SUBSCRIPTION-00022':
                        message = this.properties.cardUnacceptedError;
                        break;
                    case 'OSMS-SUBSCRIPTION-00023':
                        message = this.properties.subsDefaultError;
                        break;
                default:
                    message = this.properties?.subsDefaultError;
                    break;
                }
            }
        }
        else {
            message = this.properties.subsDefaultError;
        }
        return message;
    };

    public convertDate = function (strDate) {
        const date = new Date(strDate);
        if (!isNaN(date.getTime())) {
            let year = date.toLocaleString("en", {year: "numeric"});
            let month = date.toLocaleString("en", {month: "numeric"});
            let dt = date.toLocaleString("en", {day: "numeric"})
            return month + '/' + dt + '/' + year;
        }
    };

    convertDateByMonthName = function (strDate) {
        const date = new Date(strDate);
        if (!isNaN(date.getTime())) {
            let year = date.toLocaleString("en", {year: "numeric"});
            let month = date.toLocaleString("en", {month: "long"});
            let dt = date.toLocaleString("en", {day: "numeric"})
            return month + ' ' + dt + ', ' + year;
        }
    }

    getTrialPlanStatus(data) {
        if ((data.status.toLowerCase() === 'trial') || (data.status.toLowerCase() === 'processing' && data.previousStatus && data.previousStatus.toLowerCase() === 'trial')) {
            return true;
        }
        return false;
    }

    getPricingDetail(unformatedPricingDetail, planType) {
        let pricingDetail = '';
        if (unformatedPricingDetail) {
            pricingDetail = unformatedPricingDetail;
            if (pricingDetail.indexOf(' / ') >= 0) {
                pricingDetail = pricingDetail.replace(' / ', '/');
            }
            if (pricingDetail.indexOf('/ ') >= 0) {
                pricingDetail = pricingDetail.replace('/ ', '/');
            }
            if (pricingDetail.indexOf('for the first year') >= 0) {
                pricingDetail = pricingDetail.replace('for', ' for');
            }
        } else {
            if (planType.toLowerCase().indexOf('monthly') >= 0) {
                pricingDetail = "/month";
            } else {
                pricingDetail = "/year";
            }
        }
        return pricingDetail;
    }

    getEnvironment() {
        let environment;
        if (window.location.origin.indexOf('qa1') > 0 || window.location.origin.indexOf('qa2') > 0 || window.location.origin.indexOf('qa3') > 0 || window.location.origin.indexOf('stage') > 0) {
            environment = window.location.host.split('-')[1].split('.')[0];
        } else if (window.location.origin.indexOf('local') > 0) {
            environment = 'qa1';
        } else {
            environment = 'prod';
        }
        return environment;
    }

    paymentIframeUrl(currentEnvironment) {
        const paymentFrameURL = environment[currentEnvironment].paymentIframeURL;
        return paymentFrameURL;
    }

    public getFreshPassRegistrationLpMaster(currentEnvironment) {
      let url;
 
      if (this.banner === 'acmemarkets')  {
        url = this.getApplicationDomainURL() + environment[currentEnvironment].freshpassLPMaster_Acme;
      } else if (this.banner === 'jewelosco') {
        url = this.getApplicationDomainURL() + environment[currentEnvironment].freshpassLPMaster_Jewelosco;
      } else {
        url = this.getApplicationDomainURL() + environment[currentEnvironment].freshpassLpMaster;
      }
      return this._http.get(url, {responseType: 'text'}).pipe(
        map((data)=>{
            data = this.replaceBannerPlaceHolderText(data)
            return data;
        }),
        catchError((error) => {
            this.logService.log(`Error in getFreshPassRegistrationLpMaster: ${JSON.stringify(error)}`);
            return throwError(() => error);
        })
      );
    }

    public getFreshPassRegistrationBelowTilesMaster(currentEnvironment) {
      let url;
      
      if (this.banner === 'acmemarkets') {
        url = this.getApplicationDomainURL() + environment[currentEnvironment].belowTilesLPMaster_Acme;
      } else if (this.banner === 'jewelosco') {
        url = this.getApplicationDomainURL() + environment[currentEnvironment].belowTilesLpMaster_Jo;
      } else {
        url = this.getApplicationDomainURL() + environment[currentEnvironment].belowTilesLpMaster;
      }

      return this._http.get(url, {responseType: 'text'}).pipe(
        map((data) => {
            data = this.replaceBannerPlaceHolderText(data)
            return data;
        }),
        catchError((error) => {
            this.logService.log(`Error in getFreshPassRegistrationBelowTilesMaster: ${JSON.stringify(error)}`);
            return throwError(() => error);
        })
      )
    }

    public getFrequentlyAskedComponent(currentEnvironment) {
        let url = this.getApplicationDomainURL() + environment[currentEnvironment].freshpassContentFAQ;
        return this._http.get(url, {responseType: 'text'}).pipe(
            map((data) => {
                data = this.replaceBannerPlaceHolderText(data)
                return data;
            }),
            catchError((error) => {
                this.logService.log(`Error in getFrequentlyAskedComponent: ${JSON.stringify(error)}`);
                return throwError(() => error);
            })
        )
    }

    public getTermsComponent(currentEnvironment) {
      let url;
      if(this.banner === 'acmemarkets') {
        url = this.getApplicationDomainURL() + environment[currentEnvironment].freshpassTerms_Acme;
      } else if (this.banner === 'jewelosco') {
        url = this.getApplicationDomainURL() + environment[currentEnvironment].freshpassTerms_JO; 
      } else {
        url = this.getApplicationDomainURL() + environment[currentEnvironment].freshpassTerms;
      }
      return this._http.get(url, {responseType: 'text'}).pipe(
          map((data) => {
            data = this.replaceBannerPlaceHolderText(data)
            return data;
          }),
          catchError((error) => {
              this.logService.log(`Error in getTermsComponent: ${JSON.stringify(error)}`);
              return throwError(() => error);
          })
      );
    }

    public getB4UPlusFrequentlyAskedComponent(currentEnvironment) {
        let url = this.getApplicationDomainURL() + environment[currentEnvironment].aemB4UPlusFaqURL;
        return this._http.get(url, {responseType: 'text'}).pipe(
            map((data) => {
                data = this.replaceBannerPlaceHolderText(data)
                return data;
            }),
            catchError((error) => {
                this.logService.log(`Error in getB4UPlusFrequentlyAskedComponent: ${JSON.stringify(error)}`);
                return throwError(() => error);
            })
        )
    }

    public getB4UPlusTermsComponent(currentEnvironment) {
      const url = this.getApplicationDomainURL() + environment[currentEnvironment].aemB4UPlusTermsURL;
      return this._http.get(url, {responseType: 'text'}).pipe(
          map((data) => {
            data = this.replaceBannerPlaceHolderText(data);
            return data;
          }),
          catchError((error) => {
              this.logService.log(`Error in getB4UPlusTermsComponent: ${JSON.stringify(error)}`);
              return throwError(() => error);
          })
      );
    }

    public getPartnerData(discountCode: string, currentEnvironment: string, userInfo): Observable<any>  {
        let url = this.getApplicationDomainURL() + environment[currentEnvironment].getPartnerDiscount.url + '?discountCode=' + discountCode;
        let headerObj = environment[currentEnvironment].subscriptionHeaders.deliverySubHttpJavaOptions;

        this.replaceHeaderBanner(headerObj);
        const options = {
            headers: {
                ...headerObj,
                'x-swy-correlation-id': uuidv4(),
                'x-swy-client-name': 'web-portal',
                'x-swy-banner': this.banner,
            }
        };
        return this._http.get(url, options).pipe(
            catchError((error) => {
                this.logService.log(`Error in getPartnerData: ${JSON.stringify(error)}`);
                return throwError(() => error);
            })
        );
    }

    private handleError(errorResponse: HttpErrorResponse) {
        if (!(errorResponse.error instanceof ErrorEvent) && errorResponse.error) {
            console.error(`status: ${errorResponse.status}, ${errorResponse.statusText}`);
            const errMsg = 'Apologies, our site is currently experiencing technical difficulties, please try again later.';
            return throwError(()=>errMsg);
        }
    }

    public fetchPartnersCardData(userInfo, enablePartnersOffersV2, pageSource: string, partnerOfferType? : string): Observable<any> {
        const storeId = userInfo.j4uStoreId || userInfo.branchId || userInfo.j4u?.storeId || userInfo.storeId;
        const storeIdQueryParam = storeId ? `&storeId=${storeId}` : '';
        const baseUrl = this.getApplicationDomainURL();
        const envConfig = environment[this.getEnvironment()];

        let appendedPageSource = `&pageSource=${pageSource}` //can be banner4u or paidmembership
        let appendedPartnerOfferType = partnerOfferType ? `&partnerOfferType=${partnerOfferType}` : '' //lto or evergreen or all, default is all 
        const url = `${baseUrl}${envConfig.getPartnerOffersAllocationApi.url}?${storeIdQueryParam}${appendedPageSource}${appendedPartnerOfferType}`;
        const headerObj = enablePartnersOffersV2 ? envConfig.partnerOffersAllocationApiHeadersV2 : envConfig.partnerOffersAllocationApiHeaders;
        this.replaceHeaderBanner(headerObj);
        const options = {
            headers: {
                ...headerObj,
                ...(userInfo?.userType !== 'G' && { 'Authorization': `Bearer ${userInfo.SWY_SHOP_TOKEN}` })
            }
        };
        return this.fetchPartnersOffersData(url, options);
    }

    private fetchPartnersOffersData(url: string, options: any) {
        return this._http.get(url, options).pipe(
            map((response: any) => 
                 response?.offers
            ),
            catchError((error) => {
                this.logService.log(`Error in fetchPartnersOffersData: ${JSON.stringify(error)}`);
                return throwError(() => error);
            })
        );
    }

    public fetchAEMEasySteps(currentEnvironment) {
      const url = this.getApplicationDomainURL() + environment[currentEnvironment].snapEligibilityEasySteps;
      return this._http.get(url, {responseType: 'text'}).pipe(
        map((data) => {
            data = this.replaceBannerPlaceHolderText(data)
            return data;
        }),
        catchError((error) => {
            this.logService.log(`Error in fetchAEMEasySteps: ${JSON.stringify(error)}`);
            return throwError(() => error);
        })
      );
    }
    
    checkMobileTokenValidity(user) {
        const currentDate = new Date();
        const userClaims = this.getUserClaims(user);
        const timestampInSeconds = userClaims['exp'] + '000';
        const expiryDate = new Date(Number(timestampInSeconds));
        if (user && user['token'] && (expiryDate > currentDate)) {
            return true;
        } else {
            return false;
        }
    }

    isValidPartnerProgramType(data) {
      return (
        data?.bannerType.toLowerCase() === this.programDisplayData?.partnersOffers?.PARTNER_BANNER_FILTER &&
        this.programDisplayData?.partnersOffers?.VALID_PARTNER_PROGRAM_TYPES?.includes(data?.partnerProgramType)
      );
    }

    isValidBanner(banners: string[], currentBanner) {
      return banners.includes(currentBanner);
    }

    public getPreChurnCancelBenefitsTiles(currentEnvironment) {
        let url = this.getApplicationDomainURL() + environment[currentEnvironment].preChurnCancelBenefitsTiles;
        return this._http.get(url, {responseType: 'text'}).pipe(
            catchError((error) => {
                this.logService.log(`Error in getPreChurnCancelBenefitsTiles: ${JSON.stringify(error)}`);
                return throwError(() => error);
            })
        );
      }

    replaceBannerPlaceholders(text: string): string {
        if (typeof text !== 'string') {
            return text;
        }
        // Display-name tokens resolve from bannerConfiguration rather than
        // window.AB.userInfo, so they still substitute when AB has not populated yet
        // (mobile webviews, pre-auth loads) instead of leaving a literal "(banner4U)"
        // in the AEM copy. Falls back to the banner key so a banner missing from
        // bannerConfiguration cannot inject the string "undefined" into the content.
        const displayBanner = (properties.bannerConfiguration as any)[this.banner]?.banner || this.banner;
        const result = text
            .replace(/\(displayableBanner\)/g, displayBanner)
            .replace(/\(banner4U\)/gi, displayBanner)
            .replace(/\(bannerName\)/g, displayBanner);

        // The remaining tokens are the banner key, which only AB carries.
        if (!window['AB']?.userInfo) {
            console.warn('User info is not available in window["AB"].userInfo');
            return result;
        }
        return result
            .replace(/\(banner\)/g, window['AB'].userInfo.banner)
            .replace(/landing_pages/g, window['AB'].userInfo.banner);
    }

    replaceBannerPlaceHolderText(data) {
        if (data && data['content'] && typeof data['content'] === 'string') {
            data['content'] = this.replaceBannerPlaceholders(data['content']);
        } else if (typeof data === 'string') {
            data = this.replaceBannerPlaceholders(data);
        }
        return data;
    }

    getUserChangePlanDetails(userInfo, currentEnvironment): Observable<any> {
        const url = this.getApplicationDomainURL() + environment[currentEnvironment].getUserChangePlanApiConfig.url;
        let headerObj = environment[currentEnvironment].subscriptionHeaders.deliverySubHttpJavaOptionsV1;

        this.replaceHeaderBanner(headerObj);
        let currentTime = new Date();
            
        const options = {
            headers: {
                ...headerObj,
                'Authorization': `Bearer ${userInfo.authToken}`,
                'x-swy-correlation-id': uuidv4(),
                'x-swy-date': currentTime.toString()
            }
        };
        return this._http.get(url, options).pipe(
            catchError((error) => {
                this.logService.log(`Error in getUserChangePlanDetails: ${JSON.stringify(error)}`);
                return throwError(() => error);
            })
        );
    }
}